import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

// Pièces communes au tableau de bord et à la page Trafic (refonte de l'Admin, phase 5b) : sélecteur de période, variation,
// graphique des visiteurs par jour (colonnes), barres de classement. Une seule série, une seule couleur (l'accent) : pas de légende ;
// chaque valeur est aussi dans le tableau (bouton « Voir en tableau »), jamais seulement dans une info-bulle.

export const PERIODES = [1, 7, 30];

/** Grands nombres lisibles : 1 204 plutôt que 1204. */
export const nombre = (valeur, langue) => (typeof valeur === 'number' ? valeur.toLocaleString(langue) : valeur);

export function PeriodSwitch({ value, onChange }) {
  const { t } = useTranslation('admin');
  return (
    <div className="adm-seg" role="group" aria-label={t('stats.period')}>
      {PERIODES.map((p) => (
        <button key={p} type="button" aria-pressed={value === p} onClick={() => onChange(p)}>{t(`stats.periods.p${p}`)}</button>
      ))}
    </div>
  );
}

/** Variation en % par rapport à la période précédente ; « nouveau » quand il n'y avait rien avant. */
export function deltaDe(valeur, precedent) {
  if (!precedent) return valeur > 0 ? { kind: 'new' } : null;
  const pourcent = Math.round(((valeur - precedent) / precedent) * 100);
  return { kind: pourcent >= 0 ? 'up' : 'down', percent: Math.abs(pourcent) };
}

export function Delta({ valeur, precedent }) {
  const { t } = useTranslation('admin');
  const d = deltaDe(valeur, precedent);
  if (!d) return <small>{t('stats.noChange')}</small>;
  if (d.kind === 'new') return <small className="is-up">{t('stats.deltaNew')}</small>;
  const signe = d.kind === 'up' ? '+' : '−';
  return <small className={d.kind === 'up' ? 'is-up' : 'is-down'}>{t('stats.delta', { sign: signe, percent: d.percent })}</small>;
}

export function Kpi({ label, value, children, tone = '' }) {
  return (
    <div className={`adm-kpi ${tone}`}>
      <span>{label}</span>
      <b>{nombre(value, undefined)}</b>
      {children}
    </div>
  );
}

/** Graduation « propre » : 0, la moitié, le maximum arrondi (1, 2, 5, 10, 20, 50…). */
export function maximumPropre(max) {
  if (max <= 4) return 4;
  const puissance = 10 ** Math.floor(Math.log10(max));
  const pas = [1, 2, 5, 10].find((m) => m * puissance >= max);
  return pas * puissance;
}

const LARGEUR = 640;
const HAUTEUR = 170;
const BASE = 140;
const HAUT_UTILE = 120;

export function VisitorsChart({ days }) {
  const { t, i18n } = useTranslation('admin');
  const [actif, setActif] = useState(null);
  const [tableau, setTableau] = useState(false);
  const max = maximumPropre(Math.max(0, ...days.map((j) => j.count)));
  const slot = LARGEUR / days.length;
  const epaisseur = Math.min(24, slot - 10);
  const jourCourt = (iso) => new Date(`${iso}T12:00:00Z`).toLocaleDateString(i18n.language, { day: 'numeric', month: 'numeric', timeZone: 'UTC' });
  const jourLong = (iso) => new Date(`${iso}T12:00:00Z`).toLocaleDateString(i18n.language, { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' });
  const survol = actif === null ? null : days[actif];
  const dernier = days.length - 1;

  return (
    <div className="adm-chart">
      {!tableau && (
        <>
          <svg viewBox={`0 0 ${LARGEUR} ${HAUTEUR}`} width="100%" role="img" aria-label={t('stats.chartLabel', { count: days.length })}>
            {[0, max / 2, max].map((v) => {
              const y = BASE - Math.round((v / max) * HAUT_UTILE);
              return (
                <g key={v}>
                  <line x1="0" x2={LARGEUR} y1={y} y2={y} className="adm-grid" />
                  <text x="0" y={y - 4} className="adm-axis">{v}</text>
                </g>
              );
            })}
            {days.map((j, i) => {
              const h = j.count > 0 ? Math.max(3, Math.round((j.count / max) * HAUT_UTILE)) : 0;
              const x = i * slot + (slot - epaisseur) / 2;
              return (
                <g key={j.date}>
                  {h > 0 && (
                    // coins arrondis du côté de la donnée, base droite (chemin plutôt que rect : un rect arrondirait aussi la base)
                    <path
                      className={`adm-col${i === dernier ? ' is-last' : ''}${actif === i ? ' is-hover' : ''}`}
                      d={`M${x},${BASE} V${BASE - h + 4} Q${x},${BASE - h} ${x + 4},${BASE - h} H${x + epaisseur - 4} Q${x + epaisseur},${BASE - h} ${x + epaisseur},${BASE - h + 4} V${BASE} Z`}
                    />
                  )}
                  {/* zone de survol plus large que la colonne : au clavier comme à la souris */}
                  <rect
                    x={i * slot} y="10" width={slot} height={BASE - 10} fill="transparent" tabIndex={0}
                    aria-label={t('stats.chartPoint', { date: jourLong(j.date), count: j.count })}
                    onMouseEnter={() => setActif(i)} onMouseLeave={() => setActif(null)} onFocus={() => setActif(i)} onBlur={() => setActif(null)}
                  />
                  {(i === 0 || i === 7 || i === dernier) && <text x={i * slot + slot / 2} y="160" textAnchor="middle" className="adm-axis">{jourCourt(j.date)}</text>}
                </g>
              );
            })}
            {days[dernier].count > 0 && (
              <text x={dernier * slot + slot / 2} y={BASE - Math.max(3, Math.round((days[dernier].count / max) * HAUT_UTILE)) - 6} textAnchor="middle" className="adm-value">{days[dernier].count}</text>
            )}
          </svg>
          <p className="adm-readout" aria-live="polite">
            {survol ? <><b>{survol.count}</b> {t('stats.visitorsOn', { date: jourLong(survol.date), count: survol.count })}</> : <span>{t('stats.chartHint')}</span>}
          </p>
        </>
      )}
      {tableau && (
        <div className="adm-tbl">
          <table>
            <thead><tr><th>{t('stats.table.day')}</th><th className="num">{t('stats.table.visitors')}</th></tr></thead>
            <tbody>{[...days].reverse().map((j) => <tr key={j.date}><td>{jourLong(j.date)}</td><td className="num">{j.count}</td></tr>)}</tbody>
          </table>
        </div>
      )}
      <button type="button" className="btn btn-outline btn-sm" onClick={() => setTableau((v) => !v)} aria-pressed={tableau}>{tableau ? t('stats.viewChart') : t('stats.viewTable')}</button>
    </div>
  );
}

/** Barres horizontales de classement : le libellé en encre de texte, la valeur au bout, la barre en accent. */
export function BarList({ rows, empty }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  if (rows.length === 0) return <p className="adm-note">{empty}</p>;
  return (
    <ul className="adm-bars">
      {rows.map((r) => (
        <li key={r.key}>
          <span className="adm-bar-label">{r.label}</span>
          <span className="adm-bar-track"><i style={{ width: `${Math.max(2, Math.round((r.count / max) * 100))}%` }} /></span>
          <b>{r.count}</b>
        </li>
      ))}
    </ul>
  );
}
